import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import { buildScheduleRecurrenceSummary } from 'components/MonitoringAgent/utils/scheduleRecurrenceSummary';
import SidePanel from 'components/SidePanel/SidePanel';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import { BUTTON_VARIANT } from 'constants/interfaces';
import {
  useGetMonitoringRunFindingsQuery,
  useGetMonitoringScheduleExecutionQuery,
  useGetMonitoringScheduleExecutionsQuery,
  useGetMonitoringSessionExecutionQuery,
  useGetMonitoringSessionExecutionsQuery,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';

import type { TMonitoringExecutionStatusRollup } from 'constants/interfaces/IMonitoringExecution';
import type IScheduleRunHistoryPanel from './interfaces/IScheduleRunHistoryPanel';

import styles from './sass/ScheduleRunHistoryPanel.module.scss';

type THistoryLevel = 'executions' | 'units' | 'findings';

const formatDateTime = (value?: string): string => {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusClassName = (status: string): string => {
  switch (status) {
    case 'completed':
      return styles.statusCompleted;
    case 'running':
    case 'in_progress':
    case 'partial':
      return styles.statusRunning;
    case 'failed':
      return styles.statusFailed;
    default:
      return styles.statusSkipped;
  }
};

const getRollupLabelKey = (status: TMonitoringExecutionStatusRollup | string): string => {
  const normalized = status === 'in_progress' ? 'running' : status;
  return `valueChainMap.monitoringRunStatus${normalized[0].toUpperCase()}${normalized.slice(1)}`;
};

function ScheduleRunHistoryPanel({
  open,
  schedule,
  sessionId,
  sessionTitle,
  highlightedSessionId,
  initialExecutionKey,
  valueChainMapId,
  isLoading = false,
  isError = false,
  isRunNowLoading = false,
  onBack,
  onClose,
  onRunNow,
}: IScheduleRunHistoryPanel): React.JSX.Element {
  const { t } = useTranslation();
  const [level, setLevel] = useState<THistoryLevel>('executions');
  const [selectedExecutionKey, setSelectedExecutionKey] = useState<string | undefined>();
  const [selectedRunId, setSelectedRunId] = useState<string | undefined>();
  const [selectedUnitName, setSelectedUnitName] = useState<string | undefined>();

  const hasValidMapId = Number.isInteger(valueChainMapId) && (valueChainMapId ?? 0) > 0;
  const scheduleId = schedule?.id;
  const isSessionMode = Boolean(sessionId) && !scheduleId;
  const shouldFetchScheduleExecutions = open && hasValidMapId && Boolean(scheduleId);
  const shouldFetchSessionExecutions = open && hasValidMapId && isSessionMode;
  const shouldFetchExecutions = shouldFetchScheduleExecutions || shouldFetchSessionExecutions;

  useEffect(() => {
    if (!open) {
      return;
    }

    const startExecutionKey = isSessionMode ? initialExecutionKey : undefined;

    setLevel(startExecutionKey ? 'units' : 'executions');
    setSelectedExecutionKey(startExecutionKey);
    setSelectedRunId(undefined);
    setSelectedUnitName(undefined);
  }, [initialExecutionKey, isSessionMode, open, scheduleId, sessionId]);

  const {
    data: scheduleExecutionsResponse,
    isLoading: isScheduleExecutionsLoading,
    isError: isScheduleExecutionsError,
    refetch: refetchScheduleExecutions,
  } = useGetMonitoringScheduleExecutionsQuery(
    {
      valueChainMapId: valueChainMapId as number,
      scheduleId: scheduleId as string,
    },
    { skip: !shouldFetchScheduleExecutions },
  );

  const {
    data: sessionExecutionsResponse,
    isLoading: isSessionExecutionsLoading,
    isError: isSessionExecutionsError,
    refetch: refetchSessionExecutions,
  } = useGetMonitoringSessionExecutionsQuery(
    {
      valueChainMapId: valueChainMapId as number,
      sessionId: sessionId as number,
    },
    { skip: !shouldFetchSessionExecutions },
  );

  const {
    data: scheduleExecutionDetailResponse,
    isLoading: isScheduleUnitsLoading,
    isError: isScheduleUnitsError,
  } = useGetMonitoringScheduleExecutionQuery(
    {
      valueChainMapId: valueChainMapId as number,
      scheduleId: scheduleId as string,
      executionKey: selectedExecutionKey as string,
    },
    { skip: !shouldFetchScheduleExecutions || !selectedExecutionKey || level === 'executions' },
  );

  const {
    data: sessionExecutionDetailResponse,
    isLoading: isSessionUnitsLoading,
    isError: isSessionUnitsError,
  } = useGetMonitoringSessionExecutionQuery(
    {
      valueChainMapId: valueChainMapId as number,
      sessionId: sessionId as number,
      executionKey: selectedExecutionKey as string,
    },
    { skip: !shouldFetchSessionExecutions || !selectedExecutionKey || level === 'executions' },
  );

  const {
    data: findingsResponse,
    isLoading: isFindingsLoading,
    isError: isFindingsError,
  } = useGetMonitoringRunFindingsQuery(
    {
      valueChainMapId: valueChainMapId as number,
      runId: selectedRunId as string,
    },
    { skip: !shouldFetchExecutions || !selectedRunId || level !== 'findings' },
  );

  const executionsResponse = isSessionMode ? sessionExecutionsResponse : scheduleExecutionsResponse;
  const executionDetailResponse = isSessionMode
    ? sessionExecutionDetailResponse
    : scheduleExecutionDetailResponse;
  const isExecutionsLoading = isSessionMode
    ? isSessionExecutionsLoading
    : isScheduleExecutionsLoading;
  const isExecutionsError = isSessionMode
    ? isSessionExecutionsError
    : isScheduleExecutionsError;
  const isUnitsLoading = isSessionMode ? isSessionUnitsLoading : isScheduleUnitsLoading;
  const isUnitsError = isSessionMode ? isSessionUnitsError : isScheduleUnitsError;
  const refetchExecutions = isSessionMode
    ? refetchSessionExecutions
    : refetchScheduleExecutions;

  const executions = useMemo(
    () => executionsResponse?.data ?? [],
    [executionsResponse?.data],
  );
  const units = executionDetailResponse?.data?.units ?? [];
  const findingsPayload = findingsResponse?.data;
  const findings = findingsPayload?.findings ?? [];
  const highlightedExecutionKey = useMemo(() => {
    if (!highlightedSessionId || highlightedSessionId !== sessionId || !executions.length) {
      return undefined;
    }

    const activeExecution = executions.find((execution) => (
      execution.statusRollup === 'running'
      || execution.statusRollup === 'pending'
      || execution.statusRollup === 'partial'
    ));

    return activeExecution?.executionKey ?? executions[0]?.executionKey;
  }, [executions, highlightedSessionId, sessionId]);

  const scheduleMeta = useMemo(() => {
    if (!schedule) {
      return '';
    }

    return `${buildScheduleRecurrenceSummary(schedule.schedule)} · ${schedule.schedule.timezone}`;
  }, [schedule]);

  const showLoader = isLoading
    || (level === 'executions' && isExecutionsLoading)
    || (level === 'units' && isUnitsLoading)
    || (level === 'findings' && isFindingsLoading);

  const showError = isError
    || (level === 'executions' && isExecutionsError)
    || (level === 'units' && isUnitsError)
    || (level === 'findings' && isFindingsError);

  const handlePanelBack = () => {
    if (level === 'findings') {
      setLevel('units');
      setSelectedRunId(undefined);
      setSelectedUnitName(undefined);
      return;
    }

    if (level === 'units') {
      setLevel('executions');
      setSelectedExecutionKey(undefined);
      return;
    }

    onBack();
  };

  const handleRunNow = async () => {
    if (!onRunNow) {
      return;
    }

    await onRunNow();
    refetchExecutions();
  };

  const headerSubtitle = (() => {
    if (level === 'findings' && selectedUnitName) {
      return selectedUnitName;
    }

    if (level === 'units' && selectedExecutionKey) {
      return formatDateTime(selectedExecutionKey);
    }

    return schedule?.title ?? sessionTitle;
  })();

  const summaryText = findingsPayload?.summary
    ? JSON.stringify(findingsPayload.summary)
    : null;

  return (
    <SidePanel
      open={open}
      className={styles.container}
      ariaModal
      role="dialog"
      ariaLabel={t('valueChainMap.monitoringRunHistoryTitle')}
    >
      <div className={styles.header}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={handlePanelBack}
          aria-label={t('common.back')}
          data-i18n-key="common.back"
        >
          <ArrowLeftIcon aria-hidden="true" />
        </button>
        <div className={styles.headerText}>
          <span className={styles.headerTitle}>
            {level === 'executions' && t('valueChainMap.monitoringRunHistoryTitle')}
            {level === 'units' && t('valueChainMap.monitoringExecutionUnitsTitle')}
            {level === 'findings' && t('valueChainMap.monitoringFindingsTitle')}
          </span>
          {headerSubtitle ? (
            <span
              className={clsx(styles.headerSubtitle, {
                [styles.headerSubtitleHighlighted]: Boolean(
                  highlightedSessionId && highlightedSessionId === sessionId,
                ),
              })}
            >
              {headerSubtitle}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onClose}
          aria-label={t('common.close')}
          data-i18n-key="common.close"
        >
          <CloseIcon aria-hidden="true" />
        </button>
      </div>

      <div className={styles.body}>
        {scheduleMeta && level === 'executions' ? (
          <p className={styles.filterLabel}>{scheduleMeta}</p>
        ) : null}

        {level === 'executions' && onRunNow ? (
          <div className={styles.runNowRow}>
            <Button
              title={t('valueChainMap.monitoringScheduleRunNow')}
              labelKey="valueChainMap.monitoringScheduleRunNow"
              variant={BUTTON_VARIANT.primary}
              onClick={handleRunNow}
              className={styles.runNowButton}
              disabled={isRunNowLoading || Boolean(schedule?.paused)}
            />
            {schedule?.paused ? (
              <p className={styles.fieldHint}>{t('valueChainMap.monitoringScheduleRunNowPaused')}</p>
            ) : (
              <p className={styles.fieldHint}>{t('valueChainMap.monitoringScheduleRunNowHint')}</p>
            )}
          </div>
        ) : null}

        {showLoader ? (
          <div className={styles.loaderWrapper}>
            <SmallLoader size={30} />
          </div>
        ) : null}

        {!showLoader && showError ? (
          <p className={styles.stateMessage}>{t('valueChainMap.monitoringRunHistoryLoadError')}</p>
        ) : null}

        {!showLoader && !showError && level === 'executions' && !executions.length ? (
          <p className={styles.stateMessage}>
            {t(isSessionMode
              ? 'valueChainMap.monitoringSessionHistoryEmpty'
              : 'valueChainMap.monitoringExecutionHistoryEmpty')}
          </p>
        ) : null}

        {!showLoader && !showError && level === 'executions' && executions.length ? (
          <div className={styles.tableWrapper}>
            <table className={styles.runTable}>
              <thead>
                <tr>
                  <th>{t('valueChainMap.monitoringExecutionScheduledAt')}</th>
                  <th>{t('valueChainMap.monitoringExecutionTrigger')}</th>
                  <th>{t('valueChainMap.monitoringRunStatusColumn')}</th>
                  <th>{t('valueChainMap.monitoringExecutionUnits')}</th>
                  <th>{t('valueChainMap.monitoringRunAlerts')}</th>
                  <th>{t('valueChainMap.monitoringRunAction')}</th>
                </tr>
              </thead>
              <tbody>
                {executions.map((execution) => (
                  <tr
                    key={execution.executionKey}
                    className={clsx({
                      [styles.highlightedRow]: execution.executionKey === highlightedExecutionKey,
                    })}
                    aria-current={execution.executionKey === highlightedExecutionKey ? 'true' : undefined}
                  >
                    <td>{formatDateTime(execution.scheduledAt)}</td>
                    <td>
                      {execution.triggerType === 'manual'
                        ? t(isSessionMode
                          ? 'valueChainMap.monitoringExecutionTriggerSessionStart'
                          : 'valueChainMap.monitoringExecutionTriggerManual')
                        : t('valueChainMap.monitoringExecutionTriggerScheduled')}
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${getStatusClassName(execution.statusRollup)}`}>
                        {t(getRollupLabelKey(execution.statusRollup))}
                      </span>
                    </td>
                    <td>{execution.unitCount}</td>
                    <td>{execution.highRiskCount}</td>
                    <td>
                      <button
                        type="button"
                        className={styles.viewResultsButton}
                        onClick={() => {
                          setSelectedExecutionKey(execution.executionKey);
                          setLevel('units');
                        }}
                      >
                        {t('valueChainMap.monitoringExecutionViewUnits')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {!showLoader && !showError && level === 'units' && !units.length ? (
          <p className={styles.stateMessage}>{t('valueChainMap.monitoringExecutionUnitsEmpty')}</p>
        ) : null}

        {!showLoader && !showError && level === 'units' && units.length ? (
          <div className={styles.tableWrapper}>
            <table className={styles.runTable}>
              <thead>
                <tr>
                  <th>{t('valueChainMap.monitoringExecutionUnitName')}</th>
                  <th>{t('valueChainMap.monitoringRunStatusColumn')}</th>
                  <th>{t('valueChainMap.monitoringFindingsCount')}</th>
                  <th>{t('valueChainMap.monitoringRunAlerts')}</th>
                  <th>{t('valueChainMap.monitoringRunAction')}</th>
                </tr>
              </thead>
              <tbody>
                {units.map((unit) => (
                  <tr key={unit.runId}>
                    <td>{unit.unitName}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${getStatusClassName(unit.status)}`}>
                        {t(getRollupLabelKey(unit.status))}
                      </span>
                    </td>
                    <td>{unit.findingCount}</td>
                    <td>{unit.highRiskCount}</td>
                    <td>
                      <button
                        type="button"
                        className={styles.viewResultsButton}
                        onClick={() => {
                          setSelectedRunId(unit.runId);
                          setSelectedUnitName(unit.unitName);
                          setLevel('findings');
                        }}
                        data-i18n-key="valueChainMap.monitoring.results.viewResults"
                      >
                        {t('valueChainMap.monitoring.results.viewResults')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {!showLoader && !showError && level === 'findings' ? (
          <div className={styles.findingsSection}>
            {summaryText ? (
              <div className={styles.summaryBox}>
                <p className={styles.filterLabel}>{t('valueChainMap.monitoringFindingsSummary')}</p>
                <pre className={styles.summaryPre}>{summaryText}</pre>
              </div>
            ) : null}

            {!findings.length ? (
              <p className={styles.stateMessage}>{t('valueChainMap.monitoringFindingsEmpty')}</p>
            ) : (
              <ul className={styles.findingsList}>
                {findings.map((finding) => (
                  <li key={finding.id} className={styles.findingCard}>
                    <div className={styles.findingHeader}>
                      <span className={`${styles.statusBadge} ${getStatusClassName(finding.severity)}`}>
                        {finding.severity}
                      </span>
                      <span className={styles.findingType}>{finding.findingType}</span>
                    </div>
                    <p className={styles.findingTitle}>{finding.title}</p>
                    {finding.description ? (
                      <p className={styles.findingDescription}>{finding.description}</p>
                    ) : null}
                    {finding.sourceUrl ? (
                      <a
                        className={styles.findingLink}
                        href={finding.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('valueChainMap.monitoringFindingSource')}
                      </a>
                    ) : null}
                    {finding.impactScenarioId != null ? (
                      <p className={styles.findingImpact}>
                        {t('valueChainMap.monitoringFindingLinkedImpact', {
                          id: finding.impactScenarioId,
                        })}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>
    </SidePanel>
  );
}

export default ScheduleRunHistoryPanel;
